import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Allergen category enum. Backed by
 * `GET /api/v1/allergen-config` (api-allergy-v2-service), which returns
 * the widened taxonomy (Salt, Food, Other + Dairy, Fish, Fruits, ...)
 * derived from the backend's `constants.AllCategories`.
 *
 * Module-level cache: first call fetches; every subsequent call
 * short-circuits to the cached array. Same shape as
 * `LabUnitsService.js` — the AllergyForm mounts hydrate off the first
 * fetch; later mounts read straight from the cache.
 *
 * `FALLBACK` mirrors the last-known enum verbatim so the form can
 * render even if the backend fetch fails. Legacy shims (drug → Salt,
 * liquid → Liquids, polutry → Poultry, others → Other) live inside
 * `normaliseCategory` so already-saved rows carrying the old spellings
 * still map onto a canonical value.
 */

const FALLBACK = [
  "Salt", "Food", "Other",
  "Dairy", "Drinks", "Dry Fruits", "Fats", "Fish", "Fruits",
  "Grain", "Herbs", "Liquids", "Meat", "Nuts", "Poultry",
  "Pulses & Legumes", "Roots and Tubers", "Sea Food", "Seeds",
  "Spices", "Vegetables",
];

const LEGACY_SHIMS = {
  drug: "Salt",
  liquid: "Liquids",
  polutry: "Poultry",
  others: "Other",
};

let CACHE = null;
let LOOKUP = null;
let INFLIGHT = null;

const buildLookup = (list) => {
  const m = new Map();
  list.forEach((c) => m.set(c.toLowerCase(), c));
  Object.entries(LEGACY_SHIMS).forEach(([k, v]) => m.set(k, v));
  return m;
};

const applyList = (list) => {
  CACHE = list.slice();
  LOOKUP = buildLookup(CACHE);
  return CACHE;
};

// Seeded synchronously so the first render (before the fetch resolves)
// still has a valid dropdown + `normaliseCategory` lookup. The fetch
// replaces `CACHE` in place when it lands.
applyList(FALLBACK);

export const getCachedCategories = () => CACHE;

export const fetchCategories = () => {
  if (INFLIGHT) return INFLIGHT;
  INFLIGHT = new Promise((resolve) => {
    const svc = new APIService();
    svc.get(
      APIConfig.allergenConfig.get,
      (res) => {
        const list = res && Array.isArray(res.categories) ? res.categories : [];
        if (list.length > 0) applyList(list);
        INFLIGHT = null;
        resolve(CACHE);
      },
      () => {
        // Keep the fallback that was seeded at module load.
        INFLIGHT = null;
        resolve(CACHE);
      },
    );
  });
  return INFLIGHT;
};

export const normaliseCategory = (c) => {
  if (!c) return "Other";
  const t = String(c).trim().toLowerCase();
  return (LOOKUP && LOOKUP.get(t)) || "Other";
};
